import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useKpiDevelopPoints } from '../../../hooks/index.js';
import { formatPt } from '../../../lib/format.js';
import { SortableHeader, type SortDir } from '../KpiDetailModal/SortableHeader.js';
import type { DevelopPointDetail, KpiTeam } from '../../../types/api.js';

interface WeeklyPointDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 週開始日 (YYYY-MM-DD, 月曜) */
  weekStart: string;
  /** 週終了日 (YYYY-MM-DD, 日曜) */
  weekEnd: string;
  /** 対象チーム（未指定時は全体） */
  team?: KpiTeam;
  /** 表示用チーム名 */
  teamDisplayName: string;
}

interface AssigneeBreakdownRow {
  assignee: string;
  groupName: string | null;
  teamName: string | null;
  totalPoint: number;
  prevTotalPoint: number;
  delta: number;
  itemCount: number;
}

type WeeklyDetailSortKey =
  | 'assignee'
  | 'groupName'
  | 'teamName'
  | 'totalPoint'
  | 'delta'
  | 'itemCount';

/**
 * YYYY-MM-DD 文字列を 7 日前にシフトする (UTC 基準で計算)
 * @graph-connects none
 */
function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const ts = Date.UTC(y, m - 1, d) + days * 86_400_000;
  const dt = new Date(ts);
  const yy = String(dt.getUTCFullYear());
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * 開発ポイント詳細から担当者別の合計ptマップを構築する
 * @graph-connects none
 */
function buildPrevTotalMap(items: DevelopPointDetail[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    const key = item.assignee ?? '__unassigned__';
    map.set(key, (map.get(key) ?? 0) + item.developPoint);
  }
  return map;
}

/**
 * 文字列頻度マップから最頻値を返す（同数なら最初に出現したもの）
 * @graph-connects none
 */
function pickMode(counts: Map<string, number>): string | null {
  let best: { key: string; count: number } | null = null;
  for (const [key, count] of counts) {
    if (best === null || count > best.count) {
      best = { key, count };
    }
  }
  return best ? best.key : null;
}

/**
 * 開発ポイント詳細を担当者別に集計（グループ/チームは最頻値）。
 * `prevTotalByAssignee` を渡すと、各行に前週合計ptと前週比 delta が付与される。
 * @graph-connects pickMode [calls] グループ/チームの最頻値抽出
 */
function aggregateByAssignee(
  items: DevelopPointDetail[],
  prevTotalByAssignee?: Map<string, number>,
): AssigneeBreakdownRow[] {
  interface Acc {
    assignee: string;
    totalPoint: number;
    itemCount: number;
    groupCounts: Map<string, number>;
    teamCounts: Map<string, number>;
  }
  const map = new Map<string, Acc>();
  for (const item of items) {
    const key = item.assignee ?? '__unassigned__';
    let acc = map.get(key);
    if (!acc) {
      acc = {
        assignee: key,
        totalPoint: 0,
        itemCount: 0,
        groupCounts: new Map(),
        teamCounts: new Map(),
      };
      map.set(key, acc);
    }
    acc.totalPoint += item.developPoint;
    acc.itemCount += 1;
    if (item.assigneeGroupName) {
      acc.groupCounts.set(
        item.assigneeGroupName,
        (acc.groupCounts.get(item.assigneeGroupName) ?? 0) + 1,
      );
    }
    if (item.assigneeTeamName) {
      acc.teamCounts.set(
        item.assigneeTeamName,
        (acc.teamCounts.get(item.assigneeTeamName) ?? 0) + 1,
      );
    }
  }
  return Array.from(map.values()).map((acc) => {
    const prevTotalPoint = prevTotalByAssignee?.get(acc.assignee) ?? 0;
    return {
      assignee: acc.assignee,
      groupName: pickMode(acc.groupCounts),
      teamName: pickMode(acc.teamCounts),
      totalPoint: acc.totalPoint,
      prevTotalPoint,
      delta: acc.totalPoint - prevTotalPoint,
      itemCount: acc.itemCount,
    };
  });
}

/**
 * 週次pt集計の担当者別ドリルダウンモーダル。
 * クリックされた週の `kpi_develop_point_detail_table` を `useKpiDevelopPoints` で取得し、
 * `assignee` で集計して「誰が何ptの開発実績を出したか」をグループ/チーム付きでランキング表示する。
 * 各列ヘッダクリックで昇順/降順ソート。デフォルトは開発pt 降順。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計の担当者別ドリルダウンモーダル。クリックされた週の完了タスクを担当者・所属グループ・チーム付きで合計ptで集計し、各列ソート可能なランキングで表示。データソースは completed_date ベースの日次データ（kpi_develop_point_detail_table）である旨と、所属の決め方を注釈で明記
 * @graph-connects useKpiDevelopPoints [calls] 週内の開発ポイント詳細を取得（当週・前週の2回呼び出し）
 * @graph-connects aggregateByAssignee [calls] 担当者別に合計pt/件数/グループ/チーム/前週比を集計
 * @graph-connects buildPrevTotalMap [calls] 前週合計ptマップを構築
 * @graph-connects shiftIsoDate [calls] 前週の日付範囲を算出
 * @graph-connects SortableHeader [calls] 列ヘッダのソートUI
 * @graph-connects Modal [calls] 汎用モーダルで内容を表示
 */
export function WeeklyPointDetailModal({
  open,
  onClose,
  weekStart,
  weekEnd,
  team,
  teamDisplayName,
}: WeeklyPointDetailModalProps) {
  const { t } = useTranslation();

  const params = useMemo(() => {
    if (!open || !weekStart || !weekEnd) return null;
    return {
      team,
      startDate: weekStart,
      endDate: weekEnd,
      limit: 500,
    };
  }, [open, weekStart, weekEnd, team]);

  const prevParams = useMemo(() => {
    if (!open || !weekStart || !weekEnd) return null;
    return {
      team,
      startDate: shiftIsoDate(weekStart, -7),
      endDate: shiftIsoDate(weekEnd, -7),
      limit: 500,
    };
  }, [open, weekStart, weekEnd, team]);

  const { data, isLoading } = useKpiDevelopPoints(params);
  const { data: prevData, isLoading: isPrevLoading } = useKpiDevelopPoints(prevParams);

  const breakdown = useMemo(() => {
    if (!data?.data) return [];
    const prevTotals = buildPrevTotalMap(prevData?.data ?? []);
    return aggregateByAssignee(data.data, prevTotals);
  }, [data, prevData]);

  const [sortKey, setSortKey] = useState<WeeklyDetailSortKey>('totalPoint');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: WeeklyDetailSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'totalPoint' || key === 'itemCount' || key === 'delta' ? 'desc' : 'asc');
    }
  };

  const sortedBreakdown = useMemo(() => {
    const arr = [...breakdown];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [breakdown, sortKey, sortDir]);

  const totalPoint = useMemo(() => breakdown.reduce((s, r) => s + r.totalPoint, 0), [breakdown]);
  const totalCount = useMemo(() => breakdown.reduce((s, r) => s + r.itemCount, 0), [breakdown]);
  const totalDelta = useMemo(() => breakdown.reduce((s, r) => s + r.delta, 0), [breakdown]);

  const formatAssignee = (assignee: string): string =>
    assignee === '__unassigned__' ? t('kpi.weeklyDetail.unassigned') : assignee;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={t('kpi.weeklyDetail.title', { weekStart, weekEnd })}
      description={t('kpi.weeklyDetail.description', {
        team: teamDisplayName,
        weekStart,
        weekEnd,
        totalPt: formatPt(totalPoint),
        count: totalCount,
      })}
    >
      {isLoading || isPrevLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : breakdown.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          {t('kpi.weeklyDetail.noItems')}
        </div>
      ) : (
        <div className="space-y-3 mx-auto w-fit">
          <table className="w-auto text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  {t('kpi.weeklyDetail.rank')}
                </th>
                <th className="text-left py-2 px-2 pr-6 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <SortableHeader
                    label={t('kpi.weeklyDetail.assignee')}
                    sortKey="assignee"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 pr-6 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <SortableHeader
                    label={t('kpi.weeklyDetail.group')}
                    sortKey="groupName"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-left py-2 px-2 pr-6 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <SortableHeader
                    label={t('kpi.weeklyDetail.team')}
                    sortKey="teamName"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <div className="flex justify-end">
                    <SortableHeader
                      label={t('kpi.weeklyDetail.point')}
                      sortKey="totalPoint"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                  </div>
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <div className="flex justify-end">
                    <SortableHeader
                      label={t('kpi.weeklyDetail.delta')}
                      sortKey="delta"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                  </div>
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  {t('kpi.weeklyDetail.share')}
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                  <div className="flex justify-end">
                    <SortableHeader
                      label={t('kpi.weeklyDetail.count')}
                      sortKey="itemCount"
                      currentKey={sortKey}
                      currentDir={sortDir}
                      onSort={handleSort}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBreakdown.map((row, index) => (
                <tr
                  key={row.assignee}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-1.5 px-2 text-right text-[var(--color-text-muted)] whitespace-nowrap tabular-nums">
                    {index + 1}
                  </td>
                  <td
                    className={`py-1.5 px-2 pr-6 whitespace-nowrap ${row.assignee === '__unassigned__' ? 'text-[var(--color-text-muted)] italic' : 'text-[var(--color-text-primary)]'}`}
                  >
                    {formatAssignee(row.assignee)}
                  </td>
                  <td className="py-1.5 px-2 pr-6 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {row.groupName ?? <span className="text-[var(--color-text-muted)]">-</span>}
                  </td>
                  <td className="py-1.5 px-2 pr-6 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {row.teamName ?? <span className="text-[var(--color-text-muted)]">-</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right text-[var(--color-text-primary)] font-medium whitespace-nowrap tabular-nums">
                    {formatPt(row.totalPoint)}pt
                  </td>
                  <td
                    className={`py-1.5 px-2 text-right whitespace-nowrap tabular-nums ${
                      row.delta > 0
                        ? 'text-[var(--color-success)]'
                        : row.delta < 0
                          ? 'text-[var(--color-error)]'
                          : 'text-[var(--color-text-muted)]'
                    }`}
                  >
                    {row.delta === 0
                      ? '±0pt'
                      : `${row.delta > 0 ? '+' : '−'}${formatPt(Math.abs(row.delta))}pt`}
                  </td>
                  <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)] whitespace-nowrap tabular-nums">
                    {totalPoint > 0 ? `${((row.totalPoint / totalPoint) * 100).toFixed(1)}%` : '-'}
                  </td>
                  <td className="py-1.5 px-2 text-right text-[var(--color-text-secondary)] whitespace-nowrap tabular-nums">
                    {row.itemCount}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[var(--color-border)]">
                <td
                  colSpan={4}
                  className="py-2 px-2 text-right font-medium text-[var(--color-text-muted)]"
                >
                  {t('kpi.weeklyDetail.totalLabel')}
                </td>
                <td className="py-2 px-2 text-right font-semibold text-[var(--color-text-primary)] whitespace-nowrap tabular-nums">
                  {formatPt(totalPoint)}pt
                </td>
                <td
                  className={`py-2 px-2 text-right font-semibold whitespace-nowrap tabular-nums ${
                    totalDelta > 0
                      ? 'text-[var(--color-success)]'
                      : totalDelta < 0
                        ? 'text-[var(--color-error)]'
                        : 'text-[var(--color-text-muted)]'
                  }`}
                >
                  {totalDelta === 0
                    ? '±0pt'
                    : `${totalDelta > 0 ? '+' : '−'}${formatPt(Math.abs(totalDelta))}pt`}
                </td>
                <td className="py-2 px-2" />
                <td className="py-2 px-2 text-right font-semibold text-[var(--color-text-primary)] whitespace-nowrap tabular-nums">
                  {totalCount}
                </td>
              </tr>
            </tfoot>
          </table>
          <p className="text-xs text-[var(--color-text-muted)]">{t('kpi.weeklyDetail.note')}</p>
        </div>
      )}
    </Modal>
  );
}
